import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { LocationGroupScheduleComponentContext } from 'src/components/context';
import { Footer } from 'src/components/footer/Footer';
import { PublicationModal, SimpleDialog } from 'src/components/modal';
import { Progress } from 'src/components/progress';
import { GenericStepper } from 'src/components/stepper';
import { PATHS } from 'src/constants/paths';
import { useParamQuery } from 'src/hooks';
import { useNavigateBackHandler } from 'src/hooks/navigation';
import { PublicationTypes } from 'src/interfaces';
import { lang } from 'src/utils';

import { useScheduleComponentPage } from './hooks/ScheduleComponentPage.hook';
import AdditionalLocationGroups from './modules/additionalLocationGroups/AdditionalLocationGroups';
import { EditScheduleComponent } from './modules/editScheduleComponent/EditScheduleComponent';
import { ReviewPageContent } from './modules/reviewPageContent/ReviewPageContent';
import { ScheduleComponentContent } from './modules/scheduleComponentContentWrapper/ScheduleComponentContent';
import { getNavigateBackLink } from './ScheduleComponentPage.helper';

export const ScheduleComponentPage = () => {
  const { getParamsValue } = useParamQuery();
  const scheduleId = getParamsValue('scheduleId')[0];
  const isScheduleComponentPage = true;
  const isEditingSchedule = !!scheduleId;

  const {
    activeStep,
    contextValues,
    enabledSave,
    confirmScheduleComponentsModal,
    publicationModal,
    handleNext,
    handleBack,
    completeSteps,
    reviewPageProps,
    createSchedule,
    bulkCreateSchedule,
    updateSchedule,
    layout,
    isContentLoading,
    locationGroupId,
    additionalLocationGroups,
    publishableLocationGroupIds,
    hasLocationGroupsWithoutLocations,
    navigateAfterSaveWithoutPublication,
    clearNavigateAfterSaveWithoutPublication,
  } = useScheduleComponentPage(scheduleId);

  const location = useLocation();
  const navigateBackLink = getNavigateBackLink(location, locationGroupId);
  const { navigate, handleNavigateBackWithoutBlocker } = useNavigateBackHandler(
    navigateBackLink,
    !isContentLoading && !!locationGroupId,
  );

  useEffect(() => {
    if (!navigateAfterSaveWithoutPublication) return;

    clearNavigateAfterSaveWithoutPublication();
    handleNavigateBackWithoutBlocker();
  }, [
    clearNavigateAfterSaveWithoutPublication,
    handleNavigateBackWithoutBlocker,
    navigateAfterSaveWithoutPublication,
  ]);

  const scheduleComponentSteps = [
    {
      name: lang.scheduleComponent.steps.scheduleDetails,
      component: null,
      footer: null,
    },
    {
      name: lang.scheduleComponent.label,
      component: !isContentLoading ? (
        <ScheduleComponentContent layout={layout} />
      ) : (
        <Progress />
      ),
      footer: (
        <Footer
          showFooterActions
          showArrow
          saveLabel={lang.next}
          cancelLabel={lang.cancel}
          disableSave={!enabledSave}
          hideBackButton={isScheduleComponentPage}
          onCancel={() =>
            navigate({
              pathname: PATHS.LOCATION_GROUP_CALENDAR(locationGroupId),
            })
          }
          onSave={() => handleNext(0)}
          onBack={handleBack}
        />
      ),
    },
    {
      name: lang.reviewChanges,
      component: <ReviewPageContent {...reviewPageProps} />,
      footer: (
        <Footer
          showFooterActions
          showArrow
          saveLabel={
            publishableLocationGroupIds.length > 0
              ? lang.saveAndPublish
              : lang.save
          }
          cancelLabel={lang.cancel}
          disableSave={false}
          onBack={handleBack}
          onCancel={() =>
            navigate({
              pathname: PATHS.LOCATION_GROUP_CALENDAR(locationGroupId),
            })
          }
          onSave={completeSteps}
        />
      ),
    },
  ];

  if (additionalLocationGroups) {
    scheduleComponentSteps.splice(1, 0, {
      name: lang.scheduleComponent.additionalLocationGroups,
      component: !isContentLoading ? (
        <AdditionalLocationGroups layout={layout} />
      ) : (
        <Progress />
      ),
      footer: (
        <Footer
          showFooterActions
          showArrow
          saveLabel={lang.next}
          cancelLabel={lang.cancel}
          onCancel={() =>
            navigate({
              pathname: PATHS.LOCATION_GROUP_CALENDAR(locationGroupId),
            })
          }
          onSave={() => handleNext(1)}
        />
      ),
    });
  }

  return (
    <LocationGroupScheduleComponentContext.Provider value={contextValues}>
      {isEditingSchedule && !isContentLoading ? (
        <EditScheduleComponent
          enabledSave={enabledSave}
          handleSaveClick={completeSteps}
          saveLabel={
            publishableLocationGroupIds.length > 0
              ? lang.saveAndPublish
              : lang.save
          }
        />
      ) : (
        <GenericStepper
          steps={scheduleComponentSteps}
          activeStep={activeStep}
          connectorStyleType="solid"
          isAlternativeLabel
          headerPadding="0px 30px 20px"
        />
      )}

      <SimpleDialog
        open={confirmScheduleComponentsModal.open}
        title={
          !isEditingSchedule
            ? lang.scheduleComponentsModal.title
            : lang.scheduleComponentsEditModal.title
        }
        subTitle={lang.scheduleComponentsModal.subTitle}
        onClose={confirmScheduleComponentsModal.onCloseModal}
        onClickCancel={confirmScheduleComponentsModal.onCloseModal}
        modalSize="sm"
        onClickConfirm={confirmScheduleComponentsModal.customFn}
        isLoading={
          createSchedule.isLoading ||
          bulkCreateSchedule.isLoading ||
          updateSchedule.isLoading
        }
      />

      {publicationModal.open && (
        <PublicationModal
          open={publicationModal.open}
          publicationType={PublicationTypes.FULL}
          locationGroupIDs={publishableLocationGroupIds}
          alertMessage={
            hasLocationGroupsWithoutLocations
              ? lang.publicationModal.locationGroupsWithoutNsnsWarning
              : undefined
          }
          onClose={() => {
            publicationModal.onCloseModal();
            handleNavigateBackWithoutBlocker();
          }}
        />
      )}
    </LocationGroupScheduleComponentContext.Provider>
  );
};
